import {
  AdvisorCustomDisclosures,
  Citations,
  OnboardingLayout,
  OnboardingResultsStatusScreen,
  useSlideSetup,
  useResultsAreInSlide,
  citations,
} from '@/features/client-onboarding';
import { OnboardingOurPredictions, OnboardingCostFactors, OnboardingInsightsSummary } from '@/pages';
import { useIsConsumerSession } from '@/hooks/use-app-mode';
import { ResultsAreInSlideOutContent } from '@/components';
import { MethodologyCallout } from '@/shared/components/methodology-callout';
import { possessive } from '@shared';

import advisorImageUrl from '@/images/OnboardingResultsAreIn.png';
import { Badge } from '@design-system';
import { InfoIcon } from 'lucide-react';

export function OnboardingResultsAreIn({ clientId }: ClientIdProps) {
  const {
    clientFirstName,
    clientHasStartedLtc,
    isAlgorithmFinishedRunning,
    hasAlgorithmError,
    retryCountdown,
    handleRefresh,
  } = useResultsAreInSlide(clientId);

  if (hasAlgorithmError) {
    return (
      <OnboardingResultsStatusScreen
        variant="error"
        onRefresh={handleRefresh}
      />
    );
  }

  if (!isAlgorithmFinishedRunning) {
    return (
      <OnboardingResultsStatusScreen
        variant="pending"
        retryCountdown={retryCountdown}
        onRefresh={handleRefresh}
      />
    );
  }

  return (
    <OnboardingResultsAreInContent
      clientFirstName={clientFirstName}
      clientHasStartedLtc={clientHasStartedLtc}
    />
  );
}

interface OnboardingResultsAreInContentProps {
  clientFirstName: string;
  clientHasStartedLtc: boolean;
}

function OnboardingResultsAreInContent({
  clientFirstName,
  clientHasStartedLtc,
}: OnboardingResultsAreInContentProps) {
  return (
    <OnboardingLayout>
      <OnboardingResultsAreInSlideSetup
        clientFirstName={clientFirstName}
        clientHasStartedLtc={clientHasStartedLtc}
      />
      <div className="flex h-max flex-col items-center justify-center pb-4">
        <div className="grid grid-cols-1 items-center gap-2 pb-6 pt-0 md:grid-cols-2 md:py-16">
          <img
            src={advisorImageUrl}
            className="md:max-w-100 w-full max-w-48 md:mx-auto"
            alt="Results are in"
          />
          <div className="mb-4 flex flex-col gap-3 leading-normal tracking-wide md:gap-5">
            <h1
              data-testid="results-are-in-header"
              className="text-balance text-2xl font-bold text-gray-950 md:text-4xl"
            >
              {possessive(clientFirstName)} results are in!
            </h1>
            <p
              className="text-sm md:text-xl"
              data-testid="results-are-in-description"
            >
              {possessive(clientFirstName)} long-term care predictions are made using{' '}
              <span className="text-primary">artificial intelligence (AI)</span>.
            </p>
            <p className="text-sm md:text-xl">
              We compare {possessive(clientFirstName)} information with similar data from nearly{' '}
              <span className="text-primary">50,000</span> families.
            </p>
            <p className="text-sm md:text-xl">
              Our dataset includes over <span className="text-primary">500,000,000</span> data
              points to personalize {possessive(clientFirstName)} predictions.
            </p>
            <div className="mt-1">
              <MethodologyCallout
                variant='content'
                title="FAQ"
                content={<ResultsAreInSlideOutContent clientFirstName={clientFirstName} />}
                data-testid="results-are-in-methodology-callout"
              >
                <Badge className='md:text-base md:py-1 md:pl-2! md:pr-3! hover:bg-primary hover:border-primary-dark hover:text-primary-foreground transition-all duration-200'>
                  <InfoIcon className='md:size-4! shrink-0' />
                  Learn more about our data and methodology
                </Badge>
              </MethodologyCallout>
            </div>
          </div>
        </div>
        <div className="mt-auto pt-4">
          <Citations
            className="w-full"
            citations={[citations.OnboardingResultsAreInDisclaimer]}
          />
          <AdvisorCustomDisclosures className="w-full" />
        </div>
      </div>
    </OnboardingLayout>
  );
}

// Where Next goes. Clients already receiving care skip the predictions: in the consumer flow they go
// to the summary (their costs come later, in the flow), elsewhere to Cost Factors.
function nextSlideFor(clientFirstName: string, clientHasStartedLtc: boolean, isConsumerSession: boolean) {
  const name = possessive(clientFirstName);
  if (!clientHasStartedLtc) return { label: `See ${name} Predictions`, page: OnboardingOurPredictions };
  if (isConsumerSession) return { label: `See ${name} Summary`, page: OnboardingInsightsSummary };
  return { label: `See ${name} Costs`, page: OnboardingCostFactors };
}

function OnboardingResultsAreInSlideSetup({
  clientFirstName,
  clientHasStartedLtc,
}: OnboardingResultsAreInContentProps) {
  const next = nextSlideFor(clientFirstName, clientHasStartedLtc, useIsConsumerSession());
  useSlideSetup({
    slidePage: OnboardingResultsAreIn,
    nextButtonLabel: next.label,
    nextSlidePage: next.page,
  });

  return null;
}
